import { useCallback, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'corvus:favourite-projects';

/**
 * Reads the current favourite project keys from localStorage.
 */
function getFavourites(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    return [];
  } catch {
    return [];
  }
}

function setFavourites(keys: string[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
  window.dispatchEvent(new StorageEvent('storage', { key: STORAGE_KEY }));
}

// External store subscription for useSyncExternalStore
const subscribers = new Set<() => void>();

function subscribe(callback: () => void) {
  subscribers.add(callback);

  const handleStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY || e.key === null) {
      callback();
    }
  };

  window.addEventListener('storage', handleStorage);
  return () => {
    subscribers.delete(callback);
    window.removeEventListener('storage', handleStorage);
  };
}

let snapshotCache: string[] = getFavourites();
let snapshotJson = JSON.stringify(snapshotCache);

function getSnapshot(): string[] {
  const current = localStorage.getItem(STORAGE_KEY) ?? '[]';
  if (current !== snapshotJson) {
    snapshotJson = current;
    snapshotCache = getFavourites();
  }
  return snapshotCache;
}

function notifySubscribers() {
  snapshotJson = '';  // invalidate cache
  subscribers.forEach((cb) => cb());
}

/**
 * Hook that provides favourite project management via localStorage.
 * Returns the list of favourite project keys along with toggle/add/remove helpers.
 */
export function useFavouriteProjects() {
  const favourites = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const isFavourite = useCallback(
    (projectKey: string) => favourites.includes(projectKey),
    [favourites],
  );

  const toggleFavourite = useCallback((projectKey: string) => {
    const current = getFavourites();
    const next = current.includes(projectKey)
      ? current.filter((k) => k !== projectKey)
      : [...current, projectKey];
    setFavourites(next);
    notifySubscribers();
  }, []);

  const addFavourite = useCallback((projectKey: string) => {
    const current = getFavourites();
    if (!current.includes(projectKey)) {
      setFavourites([...current, projectKey]);
      notifySubscribers();
    }
  }, []);

  const removeFavourite = useCallback((projectKey: string) => {
    const current = getFavourites();
    if (current.includes(projectKey)) {
      setFavourites(current.filter((k) => k !== projectKey));
      notifySubscribers();
    }
  }, []);

  return {
    favourites,
    isFavourite,
    toggleFavourite,
    addFavourite,
    removeFavourite,
  };
}
